import type { AdSource, AdView, Slide } from "./types";

// HTML-escape only the small set of characters that can break attribute or
// text contexts inside the srcdoc string. Backend creatives are trusted
// (HTML may contain markup) but URLs flow through esc() to defuse quote
// injection on the anchor href.
const ESC: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function esc(s: string | undefined): string {
  if (!s) return "";
  return s.replace(/[&<>"']/g, (c) => ESC[c] ?? c);
}

// buildClickHref combines the click endpoint with the landing URL the
// browser should navigate to once /click has fired its tracking event.
// An ad is never decorative: it carries meaning and wraps a link, so an empty
// alt leaves a screen reader announcing a link with no name at all (WCAG 2.2
// SC 1.1.1 and SC 2.4.4, both level A). Falls back to the title when the
// advertiser wrote no alternative text.
function altOf(ad: { alt_text?: string; title?: string }): string {
  return esc(ad.alt_text || ad.title || "Anuncio");
}

export function buildClickHref(clickEndpoint: string, landingURL?: string): string {
  if (!landingURL) return clickEndpoint;
  const sep = clickEndpoint.includes("?") ? "&" : "?";
  return clickEndpoint + sep + "u=" + encodeURIComponent(landingURL);
}

const SANDBOX = "allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation";

export function isMediation(source: AdSource): boolean {
  return source === "mediation" || source === "platform_mediation";
}

// sandboxFor keeps first-party creatives as static markup (no allow-scripts).
// A bidder's markup is a script tag more often than not and blank without
// one, so a mediation fill gets allow-scripts — never allow-same-origin, so
// it still runs in an opaque origin with no reach into the host page.
export function sandboxFor(source: AdSource): string {
  return isMediation(source) ? "allow-scripts " + SANDBOX : SANDBOX;
}

// clickTrackerScript fires the bidder's click pixels on the first click inside
// the frame; only reachable in a mediation frame, the one that may run script.
function clickTrackerScript(urls: string[] | undefined): string {
  if (!urls || urls.length === 0) return "";
  const list = JSON.stringify(urls).replace(/</g, "\\u003c");
  return (
    "<script>(function(u,d){document.addEventListener('click',function(){if(d)return;d=1;" +
    "u.forEach(function(s){new Image().src=s})},true)})(" +
    list +
    ",0)</script>"
  );
}

// buildIframeSrcdoc returns the HTML written to the iframe via srcdoc. A
// first-party creative is drawn in a sandbox without allow-scripts, so it runs
// as opaque null-origin static markup — XSS-safe even for HTML creatives that
// include script tags. A bidder's HTML is written as-is, without our anchor
// around it: it carries its own click handling.
export function buildIframeSrcdoc(
  ad: AdView,
  clickHref: string,
  isTest: boolean,
  mediation = false,
): string {
  const w = ad.width > 0 ? ad.width : 300;
  const h = ad.height > 0 ? ad.height : 250;
  const href = esc(clickHref);
  let body = "";
  if ((ad.type === "video" || ad.type === "video_rewarded") && ad.video_url) {
    body =
      '<a href="' +
      href +
      '" target="_top" rel="noopener noreferrer nofollow" style="display:block">' +
      '<video src="' +
      esc(ad.video_url) +
      '"' +
      (ad.main_image_url ? ' poster="' + esc(ad.main_image_url) + '"' : "") +
      ' width="' +
      w +
      '" height="' +
      h +
      '" autoplay muted loop playsinline ' +
      'style="display:block;width:100%;height:100%;object-fit:cover;border:0"></video>' +
      "</a>";
  } else if (ad.type === "audio" && (ad.audio_url || ad.video_url)) {
    const cover = ad.main_image_url
      ? '<a href="' +
        href +
        '" target="_top" rel="noopener noreferrer nofollow" style="display:block;width:100%;height:100%">' +
        '<img src="' +
        esc(ad.main_image_url) +
        '" alt="' +
        altOf(ad) +
        '" style="width:100%;height:100%;object-fit:cover;display:block;border:0"></a>'
      : "";
    body =
      cover +
      '<audio src="' +
      esc(ad.audio_url ?? ad.video_url) +
      '" controls style="position:absolute;left:0;right:0;bottom:0;width:100%"></audio>';
  } else if (ad.type === "carousel" && ad.slides && ad.slides.length > 0) {
    body = carouselBody(ad.slides, href, w, h);
  } else if (ad.type === "image" && ad.asset_url) {
    body =
      '<a href="' +
      href +
      '" target="_top" rel="noopener noreferrer nofollow">' +
      '<img src="' +
      esc(ad.asset_url) +
      '" width="' +
      w +
      '" height="' +
      h +
      '" alt="' +
      altOf(ad) +
      '" style="display:block;border:0">' +
      "</a>";
  } else if (ad.html && mediation) {
    body = ad.html;
  } else if (ad.html) {
    // Covers html, template (server converts to html) and native (server
    // renders a self-contained HTML fallback in ad.html). Gating on ad.html
    // instead of the type keeps every server-rendered creative visible.
    body =
      '<a href="' +
      href +
      '" target="_top" rel="noopener noreferrer nofollow" ' +
      'style="display:block;text-decoration:none;color:inherit">' +
      ad.html +
      "</a>";
  }
  const badge = isTest
    ? '<div style="position:absolute;top:4px;right:4px;background:rgba(220,38,38,0.92);' +
      "color:#fff;font:600 10px/1 system-ui,-apple-system,sans-serif;" +
      'padding:3px 6px;border-radius:3px;z-index:2147483647">TEST</div>'
    : "";
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<meta name="referrer" content="no-referrer">' +
    "<style>html,body{margin:0;padding:0;width:100%;height:100%;" +
    "overflow:hidden;background:transparent}" +
    CAROUSEL_CSS +
    "</style></head>" +
    '<body><div style="position:relative;width:' +
    w +
    "px;height:" +
    h +
    'px">' +
    body +
    badge +
    "</div>" +
    (mediation ? clickTrackerScript(ad.click_trackers) : "") +
    "</body></html>"
  );
}

// The tag renders inside a sandbox without allow-scripts, so the deck has to
// swipe with zero JavaScript: a flex track with CSS scroll-snap gives native
// momentum scrolling on touch and trackpad, and each card is a plain anchor
// to the same /click endpoint (one advertiser, one auction, one impression).
const CAROUSEL_CSS =
  ".apc{display:flex;width:100%;height:100%;overflow-x:auto;overflow-y:hidden;" +
  "scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;" +
  "scrollbar-width:none}" +
  ".apc::-webkit-scrollbar{display:none}" +
  ".apc>a{flex:0 0 100%;scroll-snap-align:start;position:relative;display:block;" +
  "text-decoration:none;color:inherit}" +
  ".apc img{display:block;width:100%;height:100%;object-fit:cover;border:0}" +
  ".apc-cap{position:absolute;left:0;right:0;bottom:0;padding:6px 8px;" +
  "background:rgba(17,24,39,0.82);color:#fff;" +
  "font:400 11px/1.35 system-ui,-apple-system,sans-serif}" +
  ".apc-cap b{display:block;font-weight:600;font-size:12px}" +
  ".apc-cta{display:inline-block;margin-top:4px;padding:2px 6px;border-radius:3px;" +
  "background:#fff;color:#111827;font-weight:600;font-size:10px}" +
  ".apc-n{position:absolute;top:4px;left:4px;padding:2px 5px;border-radius:3px;" +
  "background:rgba(17,24,39,0.7);color:#fff;" +
  "font:500 10px/1 system-ui,-apple-system,sans-serif}";

function carouselBody(slides: Slide[], href: string, w: number, h: number): string {
  const cards: string[] = [];
  for (let i = 0; i < slides.length; i += 1) {
    const s = slides[i];
    if (!s || !s.asset_url) continue;
    let caption = "";
    if (s.title || s.body || s.cta_text) {
      caption =
        '<div class="apc-cap">' +
        (s.title ? "<b>" + esc(s.title) + "</b>" : "") +
        (s.body ? esc(s.body) : "") +
        (s.cta_text ? '<span class="apc-cta">' + esc(s.cta_text) + "</span>" : "") +
        "</div>";
    }
    cards.push(
      '<a href="' +
        href +
        '" target="_top" rel="noopener noreferrer nofollow">' +
        '<img src="' +
        esc(s.asset_url) +
        '" width="' +
        w +
        '" height="' +
        h +
        '" alt="' +
        esc(s.title) +
        '" loading="' +
        (i === 0 ? "eager" : "lazy") +
        '">' +
        caption +
        '<span class="apc-n">' +
        (i + 1) +
        "/" +
        slides.length +
        "</span>" +
        "</a>",
    );
  }
  if (cards.length === 0) return "";
  return '<div class="apc">' + cards.join("") + "</div>";
}
